import { useLingui } from '@lingui/react/macro'
import { Drawer } from 'antd'
import Cantilever from 'assets/cantilever-logo.svg?react'
import { CantileverButton } from 'components/CantileverButton'
import { X } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

import { MidSection } from './MidSection'
import { SelectedTab } from './types'

interface MenuDrawerProps {
  isOpen: boolean
  onClose: () => void
  siteID: string | undefined
  orgID: string | undefined
  selectedTab: SelectedTab | undefined
  children?: React.ReactNode
}

const CLOSE_BUTTON_PADDING = '0 6px'

const StyledDrawer = styled(Drawer)`
  .ant-drawer-content {
    border-radius: 0 16px 16px 0;
    overflow: hidden;
  }

  .ant-drawer-content-wrapper {
    transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  .ant-drawer-mask {
    transition: opacity 200ms ease !important;
  }

  .ant-drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 3.5rem;
    padding: 0 1rem;
    background-color: ${({ theme }) => theme.forecast.background3};
    border-bottom: none;
  }

  .ant-drawer-body {
    padding: 0;
    background-color: ${({ theme }) => theme.forecast.background3};
  }

  /* Hide the default close icon; we render our own in the header extra slot */
  .ant-drawer-close {
    display: none !important;
  }
`

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  height: 3.5rem;
`

const GeckoLogo = styled(Cantilever)`
  height: 32px;
  width: 32px;

  path {
    fill: ${({ theme }) => theme.forecast.tertiaryText};
    fill-opacity: 1;
  }
`

const DrawerContent = styled.div`
  padding: 0.5rem;
`

export const MenuDrawer: React.FC<MenuDrawerProps> = ({
  isOpen,
  onClose,
  siteID,
  orgID,
  selectedTab,
  children,
}) => {
  const { t } = useLingui()
  return (
    <StyledDrawer
      title={
        <DrawerHeader>
          <GeckoLogo />
        </DrawerHeader>
      }
      extra={
        <CantileverButton
          aria-label={t`Close navigation`}
          onClick={onClose}
          ghost
          padding={CLOSE_BUTTON_PADDING}
          style={{ height: '2rem' }}>
          <X size={16} />
        </CantileverButton>
      }
      placement='left'
      open={isOpen}
      onClose={onClose}
      closable={false}
      size={300}
      mask={true}
      maskClosable={true}>
      <DrawerContent>
        {children}
        <MidSection
          siteID={siteID}
          orgID={orgID}
          selectedTab={selectedTab}
          onCloseDrawer={onClose}
          autoFocus={isOpen}
        />
      </DrawerContent>
    </StyledDrawer>
  )
}
